import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { Job } from '../shared/api';
import JobsTable, { sortJobs, type SortKey } from '../components/JobsTable';
import { Banner, Button, Card, Select, Spinner } from '../components/ui';
import { api } from '../lib/api';
import { centsToDollars, daysUntil, OPEN_STATUSES } from '../lib/format';
import { useSession } from '../lib/session';
import { useAsync } from '../lib/useAsync';

function StatCard({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-5 py-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-dim">{label}</p>
      <p className={`mt-1.5 text-3xl font-bold tabular-nums ${tone ?? 'text-ink'}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </div>
  );
}

/** NOOs still to send inside the next 7 days. Already-sent NOOs stop counting. */
export function noosDueThisWeek(jobs: Job[]): Job[] {
  return jobs.filter((j) => {
    if (j.nooSentAt || !j.nooDeadline) return false;
    const d = daysUntil(j.nooDeadline);
    return d !== null && d <= 7;
  });
}

/** Claims of Lien still to file inside the next 30 days. */
export function liensDueThisMonth(jobs: Job[]): Job[] {
  return jobs.filter((j) => {
    if (j.lienFiled || !j.lienDeadline) return false;
    const d = daysUntil(j.lienDeadline);
    return d !== null && d <= 30;
  });
}

export default function Overview() {
  const { contractor } = useSession();
  const [sort, setSort] = useState<SortKey>('deadline');
  const { data, loading, error } = useAsync(() => api.get<{ jobs: Job[] }>('/api/jobs'), []);

  // Run 8 first-run interview: an admin whose office has never seen it (the
  // backend keeps officeSetupCompletedAt null until finish or skip) goes there
  // once. Strictly null — accounts predating the field never bounce.
  if (contractor?.permissionLevel === 'admin' && contractor.officeSetupCompletedAt === null) {
    return <Navigate to="/office-setup" replace />;
  }

  if (loading) return <Spinner label="Loading your jobs…" />;
  if (error) return <Banner tone="urgent" title="Could not load jobs">{error}</Banner>;

  const jobs = data?.jobs ?? [];
  const active = jobs.filter((j) => OPEN_STATUSES.includes(j.status));
  const noos = noosDueThisWeek(jobs);
  const liens = liensDueThisMonth(jobs);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink">Overview</h1>
          <p className="text-sm text-muted">Every job on {contractor?.companyName}, soonest deadline first.</p>
        </div>
        <Link to="/jobs/new">
          <Button>New Job</Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Active jobs" value={String(active.length)} hint={`${jobs.length} total`} />
        <StatCard
          label="NTOs due this week"
          value={String(noos.length)}
          hint="Unsent, within 7 days"
          tone={noos.length ? 'text-soon' : 'text-ink'}
        />
        <StatCard
          label="Liens due this month"
          value={String(liens.length)}
          hint="Unfiled, within 30 days"
          tone={liens.length ? 'text-urgent' : 'text-ink'}
        />
        <StatCard
          label="Mail balance"
          value={centsToDollars(contractor?.mailBalanceCents ?? 0)}
          hint={contractor?.mailAckAt ? 'Certified mail acknowledged' : 'Acknowledgment required'}
          tone={(contractor?.mailBalanceCents ?? 0) >= 2500 ? 'text-ok' : 'text-urgent'}
        />
      </div>

      <Card
        title="All jobs"
        action={
          <label className="flex items-center gap-2 text-xs text-dim">
            Sort by
            <Select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className="w-40 py-1" aria-label="Sort jobs">
              <option value="deadline">Next deadline</option>
              <option value="status">Status</option>
              <option value="address">Address</option>
            </Select>
          </label>
        }
      >
        <JobsTable jobs={sortJobs(jobs, sort)} empty="No jobs yet. Create one to start the deadline clock." />
      </Card>

      {noos.length > 0 && (
        <Banner tone="soon" title={`${noos.length} Notice to Owner${noos.length === 1 ? '' : 's'} due within 7 days`}>
          {noos.map((j) => j.propertyAddress).join(' · ')}
        </Banner>
      )}
    </div>
  );
}
